import React, { useEffect, useState, useMemo } from "react";
import apiClient from '../utils/apiClient';
import Swal from "sweetalert2";
import { SERVER_BASE } from '../config/server';
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faPlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';
const LocationList = ({ permissions }) => {
  const [locations, setLocations] = useState([]); // Location data
  const [location, setLocation] = useState(""); // Location input
  const [remarks, setRemarks] = useState(""); // Remarks input
  const [editId, setEditId] = useState(null); // ID of the Location being edited
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role')
  const email = localStorage.getItem('email');
  const [loadingStores, setLoadingStores] = useState(true);
  // Fetch all Locations from the server
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = () => {
    setLoadingStores(true);
    apiClient
      .get(SERVER_BASE + "/getLocations", {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setLocations(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingStores(false); // ✅ hide loading
      });
  };

  // Add or Update a Location
  const handleSaveLocation = () => {
    if (!location) {
      Swal.fire("Error", "Location is required.", "error");
      return;
    }

    Swal.fire({
      title: editId ? "Confirm Update" : "Confirm Add",
      text: editId
        ? "Are you sure you want to update this Location?"
        : "Are you sure you want to add this Location?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        if (editId) {
          // Update Location
          apiClient
            .put(`${SERVER_BASE}/updateLocations/${editId}`, { location, remarks, email }, {
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              }
            })
            .then(() => {
              Swal.fire("Success", "Location updated successfully.", "success");
              fetchLocations();
              resetForm();
            })
            .catch((err) => {
              Swal.fire("Error", err.response?.data?.message || "An error occurred.", "error");
            });
        } else {
          // Add Location
          apiClient
            .post(SERVER_BASE + "/addLocations", { location, remarks, code, email }, {
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              }
            })
            .then((response) => {
              Swal.fire("Success", "Location added successfully.", "success");
              fetchLocations();
              resetForm();
            })
            .catch((err) => {
              Swal.fire("Error", err.response?.data?.message || "An error occurred.", "error");
            });
        }
      }
    });
  };


  // Delete Location
  const handleDeleteLocation = (id) => {
    
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        apiClient
          .delete(`${SERVER_BASE}/deleteLocations/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Add the JWT token in the header
            }
          })
          .then(() => {
            Swal.fire("Deleted!", "Your Location has been deleted.", "success");
            fetchLocations();
          })
          .catch((error) => {
            Swal.fire("Error", "An error occurred while deleting the Location.", "error");
          });
      }
    });
  };

  // Edit Location
  const handleEditLocation = (location) => {
    setEditId(location.id);
    setLocation(location.location);
    setRemarks(location.remarks);
  };

  // Reset form
  const resetForm = () => {
    setEditId(null);
    setLocation("");
    setRemarks("");
  };

  // React-table setup
  const columns = useMemo(
    () => [
      {
        Header: 'S.No',
        Cell: ({ row }) => row.index + 1,
      },
      { Header: "Location", accessor: "location" },
      { Header: "Remarks", accessor: "remarks" },
      {
        Header: "#",
        accessor: "Action",
        Cell: ({ row }) => {
          const {
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
          } = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {(role === "itManager" || permissions?.edit) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEditLocation(row.original)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Edit
                    </button>
                  </li>
                )}

                {(role === "itManager" || permissions?.delete) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDeleteLocation(row.original.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}
                {(createdByName || updatedByName) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
    ],
    [permissions]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: locations,
      initialState: { pageIndex: 0, pageSize: 20 }, // min-20 page size
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  if (loadingStores) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 fs-5">Please wait...</div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      {(role == 'itManager' || permissions?.write) && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Stores and Areas</h3>
            <div className="ledger-count">
              Total Locations: <strong>{locations.length}</strong>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault(); // Prevent default form submission
              handleSaveLocation();
            }}
            className="formcontainer mb-4"
          >
            <div className="row">
              {/* Location Field */}
              <div className="col-md-3">
                <label htmlFor="location" className="form-label">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  className="form-control"
                  placeholder="Enter Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>

              {/* Remarks Field */}
              <div className="col-md-3">
                <label htmlFor="remarks" className="form-label">
                  Remarks
                </label>
                <input
                  type="text"
                  id="remarks"
                  className="form-control"
                  placeholder="Enter Remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="col-md-4 d-flex align-items-end">
                <button type="submit" className="btn btn-outline-success me-2">
                  {editId ? "Update Store" : "Add Store"}
                </button>
                {editId && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </>
      )}

      {/* React Table */}
      {(role == 'itManager' || permissions?.read || permissions?.edit || permissions?.delete) && (
        <div className="formcontainer">
          <div className="d-flex justify-content-between gap-3">
            <input
              className="form-control"
              placeholder="Search"
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <label className="d-inline-flex align-items-center ">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="form-select form-select w-auto"
              >
                {[20, 30, 50, 100].map((size) => (
                  <option key={size} value={size}>
                   Show {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="table-responsive">
            <table className="table table-striped" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render("Header")}
                        {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center mb-3">
            {/* Previous – left corner */}
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-secondary me-auto"
              style={{ minWidth: 110 }}   // keeps width consistent
            >
              Previous
            </button>

            {/* Center block for page index & size selector */}
            <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
              {/* Row 1: Page index and size selector in one line */}
              <div className="d-flex align-items-center gap-3">
                <div className="fw-semibold">
                  Page {pageIndex + 1} of {pageOptions.length}
                </div>


              </div>
            </div>


            {/* Next – right corner */}
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-secondary ms-auto"
              style={{ minWidth: 110 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationList;
